import React from "react";
import ContractingFooter from "features/contracting/components/ContractingFooter";
import { useForm } from "features/shared/forms/hooks/useForm";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import ContactPersonDTO from "features/contractualAccounts/dtos/ContactPersonDTO";
import { useQuery } from "data/hooks/useQuery";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { Customer_PersonsInRelationship } from "data/endpoints";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { useTranslation } from "react-i18next";
import { ContactPersonType } from "features/contractualAccounts/enums/contactPersonType";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { contractingPrepareFormValidations } from "./contractingPrepareFormValidation";
import PersonsInRelationshipSubForm from "features/contracting/components/PersonsInRelationshipSubForm";
import { Commodity } from "features/shared/enums/commodity";
import { useContractingAdd } from "features/contracting/hooks/useContractingAdd";
import CustomerDataStep from "features/contracting/models/steps/customerData/CustomerDataStep";
import { getBusinessPerson } from "./contractingPrepareFormHelper";

type Props = {
  commodity: Commodity;
};

const ContractingPrepareFormSection = ({ commodity }: Props) => {
  // translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  // form hook
  const formHandler = useForm({
    initialValidations: contractingPrepareFormValidations(),
  });

  const { getFormValue, valideteForm } = {
    ...formHandler,
  };

  // add contracting hook
  const { onAddCompanyClick } = useContractingAdd("/contracting/add");

  // fetch data
  const { data: persons, loadStatus } = useQuery<ContactPersonDTO[]>(
    {
      url: Customer_PersonsInRelationship,
      routeParams: getRouteParams("customerId", customer.customerId!),
      params: getQueryParams("ContactPersonType", [
        ContactPersonType.PersonAuthorizedToSign,
        ContactPersonType.ContactPerson,
      ]),
    },
    [],
    [customer.customerId]
  );

  // handlers
  const nextClickHandler = async () => {
    if (valideteForm()) {
      const authorizedPerson = persons.find(
        (x) => x.contactPersonId === getFormValue<string>("authorizedPersonId")
      );
      const contactPerson = persons.find(
        (x) => x.contactPersonId === getFormValue<string>("contactPersonId")
      );

      if (authorizedPerson) {
        const data: CustomerDataStep = {
          authorizedAgent: getBusinessPerson(authorizedPerson),
          contactPersonIsSameAsAuthorizedAgent: contactPerson ? false : true,
          contactPerson: contactPerson
            ? getBusinessPerson(contactPerson)
            : undefined,
        };

        await onAddCompanyClick(commodity, data);
      }
    }
  };

  // helpers
  const isEmptyPersons = () => {
    if (persons.length < 1) {
      return true;
    }

    const authorizedPersons = persons.filter(
      (x) => x.contactPersonType === "PersonAuthorizedToSign"
    );
    if (authorizedPersons.length < 1) {
      return true;
    }

    return false;
  };

  // render
  return (
    <NoContentWrapper
      data={persons}
      loadStatus={loadStatus}
      noContentInfo={tLabels("contractingPrepareFormSection.personsNotFound")}
      noContentAsAlert
      isEmpty={isEmptyPersons}
    >
      <PersonsInRelationshipSubForm
        persons={persons}
        formHandler={formHandler}
      />
      <ContractingFooter onNextClick={nextClickHandler} />
    </NoContentWrapper>
  );
};

export default ContractingPrepareFormSection;
